import { GetAssetsRequestDTO, GetQRCodesRequestDTO, GetUnitReadingsRequestDTO, RecordState, TagEntity } from '@gearbelt/global';
import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';

import { ApiService } from '@/services/api-v2';
import { AWAApiV2Service } from '@/services/awa-api-v2';

export const assetKeys = {
  all: ['assets'] as const,
  list: (limit: number, isAWA: boolean, filters?: GetAssetsRequestDTO['filters']) => [...assetKeys.all, 'list', limit, isAWA, filters] as const,
  detail: (assetId: string | undefined, isAWA: boolean) => [...assetKeys.all, 'detail', assetId, isAWA] as const,
  infinite: (filters?: GetAssetsRequestDTO['filters'], groupBy?: GetAssetsRequestDTO['groupBy'], orderBy?: GetAssetsRequestDTO['orderBy']) =>
    [...assetKeys.all, 'infinite', filters, groupBy, orderBy] as const,
};

export const manufacturerKeys = {
  all: ['manufacturers'] as const,
};

export const tagKeys = {
  all: ['tags'] as const,
  list: (entity: TagEntity | undefined, isAWA: boolean) => [...tagKeys.all, entity, isAWA] as const,
};

export const qrCodeKeys = {
  all: ['qr-codes'] as const,
  list: (fields: NonNullable<NonNullable<GetQRCodesRequestDTO['filters']>['fields']>) => [...qrCodeKeys.all, fields] as const,
};

export const sitemateUserCurrentKeys = {
  all: ['sitemate-user-current'] as const,
  detail: (isAWA: boolean) => [...sitemateUserCurrentKeys.all, isAWA] as const,
};

export const assetReadingLogKeys = {
  all: ['asset-reading-logs'] as const,
  infinite: (filters?: GetUnitReadingsRequestDTO['filters'], orderBy?: GetUnitReadingsRequestDTO['orderBy']) =>
    [...assetReadingLogKeys.all, 'infinite', filters, orderBy] as const,
};

export const getAssetsQueryOptions = ({
  limit = 100,
  isAWA = false,
  filters,
}: {
  limit?: number;
  isAWA?: boolean;
  filters?: GetAssetsRequestDTO['filters'];
} = {}) => {
  return queryOptions({
    queryKey: assetKeys.list(limit, isAWA, filters),
    queryFn: async () => (await (await (isAWA ? AWAApiV2Service : ApiService).GetAssets({ limit, filters })).json()).data,
  });
};

export const getAssetByIdQueryOptions = ({ assetId, isAWA = false }: { assetId: string | undefined; isAWA?: boolean }) => {
  return queryOptions({
    queryKey: assetKeys.detail(assetId, isAWA),
    queryFn: async () => (assetId ? (await (await (isAWA ? AWAApiV2Service : ApiService).GetAssetById(assetId)).json()).asset : null),
    enabled: !!assetId,
  });
};

export const getManufacturersQueryOptions = () => {
  return queryOptions({
    queryKey: manufacturerKeys.all,
    queryFn: async () => (await (await ApiService.GetManufacturers({ limit: 500 })).json()).data,
  });
};

export const getTagsQueryOptions = ({ entity, isAWA = false }: { entity?: TagEntity; isAWA?: boolean } = {}) => {
  return queryOptions({
    queryKey: tagKeys.list(entity, isAWA),
    queryFn: async () =>
      (await (await (isAWA ? AWAApiV2Service : ApiService).GetTags({ limit: 500, ...(entity ? { filters: { fields: { entity } } } : {}) })).json()).data,
  });
};

export const getQRCodesQueryOptions = ({ assetState, assetIds }: { assetState?: RecordState; assetIds?: string[] } = {}) => {
  const fields: NonNullable<NonNullable<GetQRCodesRequestDTO['filters']>['fields']> = {};
  if (assetState) fields.assetState = assetState;
  if (assetIds) fields.assetIds = assetIds;

  return queryOptions({
    queryKey: qrCodeKeys.list(fields),
    queryFn: async () => (await (await ApiService.GetQRCodes({ limit: 500, filters: { fields } })).json()).data,
    enabled: assetIds ? assetIds.length > 0 : true,
  });
};

export const getSitemateUserQueryOptions = ({ isAWA = false }: { isAWA?: boolean } = {}) => {
  return queryOptions({
    queryKey: sitemateUserCurrentKeys.detail(isAWA),
    queryFn: async () => (await (isAWA ? AWAApiV2Service.GetSitemateUser() : ApiService.GetCurrentSitemateUser())).json(),
  });
};

export const getAssetsInfiniteQueryOptions = ({
  filters,
  groupBy,
  orderBy,
  enabled = true,
}: {
  filters?: GetAssetsRequestDTO['filters'];
  groupBy?: GetAssetsRequestDTO['groupBy'];
  orderBy?: GetAssetsRequestDTO['orderBy'];
  enabled?: boolean;
}) => {
  return infiniteQueryOptions({
    queryKey: assetKeys.infinite(filters, groupBy, orderBy),
    queryFn: async ({ pageParam }: { pageParam?: string }) =>
      await (await ApiService.GetAssets({ limit: 30, cursors: pageParam, filters, groupBy, orderBy })).json(),
    getNextPageParam: (lastPage) => (lastPage.pageInfo.hasNextPage ? lastPage.pageInfo.endCursor : undefined),
    initialPageParam: undefined as string | undefined,
    retry: false,
    gcTime: 0,
    enabled,
  });
};

export const getAssetReadingLogsInfiniteQueryOptions = ({
  filters,
  orderBy,
  enabled = true,
}: {
  filters?: GetUnitReadingsRequestDTO['filters'];
  orderBy?: GetUnitReadingsRequestDTO['orderBy'];
  enabled?: boolean;
}) => {
  return infiniteQueryOptions({
    queryKey: assetReadingLogKeys.infinite(filters, orderBy),
    queryFn: async ({ pageParam }: { pageParam?: string }) =>
      await (await ApiService.GetUnitReadings({ limit: 30, cursors: pageParam, filters, orderBy })).json(),
    getNextPageParam: (lastPage) => (lastPage.pageInfo.hasNextPage ? lastPage.pageInfo.endCursor : undefined),
    initialPageParam: undefined as string | undefined,
    retry: false,
    gcTime: 0,
    enabled,
  });
};
